import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert
} from '@mui/material';
import {
  PlayArrow,
  CheckCircle,
  Error,
  AccessTime,
  Timeline,
  ExpandMore,
  Speed,
  AccountTree,
  ZoomIn,
  ZoomOut,
  CenterFocusStrong,
  MyLocation
} from '@mui/icons-material';
import BpmnViewer from 'bpmn-js/lib/NavigatedViewer';
import { 
  BpmnTimelineProps, 
  TimelineEvent, 
  TimelineEventType, 
  EventStatus 
} from '../types/timeline';
import { ProcessVariable } from '../types/process';
import ActivityVariables from './ActivityVariables';
import { ActivityIncidents } from './ActivityIncidents';

// Cores para diferentes tipos de eventos
const EVENT_COLORS = {
  [TimelineEventType.PROCESS_START]: '#4caf50',
  [TimelineEventType.PROCESS_END]: '#f44336',
  [TimelineEventType.ACTIVITY_START]: '#2196f3',
  [TimelineEventType.ACTIVITY_END]: '#2196f3',
  [TimelineEventType.TASK_CREATED]: '#ff9800',
  [TimelineEventType.TASK_COMPLETED]: '#4caf50',
  [TimelineEventType.VARIABLE_UPDATE]: '#9c27b0',
  [TimelineEventType.EXTERNAL_TASK_CREATED]: '#00bcd4',
  [TimelineEventType.EXTERNAL_TASK_COMPLETED]: '#4caf50',
  [TimelineEventType.EXTERNAL_TASK_FAILED]: '#f44336',
  [TimelineEventType.GATEWAY_PASSED]: '#607d8b',
  [TimelineEventType.ERROR_OCCURRED]: '#f44336'
};

const STATUS_COLORS = {
  [EventStatus.STARTED]: '#ff9800',
  [EventStatus.COMPLETED]: '#4caf50',
  [EventStatus.FAILED]: '#f44336',
  [EventStatus.CANCELLED]: '#9e9e9e',
  [EventStatus.ACTIVE]: '#2196f3'
};

const BpmnTimeline: React.FC<BpmnTimelineProps> = ({
  timeline,
  bpmnXml,
  onEventClick,
  onActivityClick,
  showOnlyExecutedPath = false,
  variables = [],
  incidents = []
}) => {
  const bpmnContainerRef = useRef<HTMLDivElement>(null);
  const bpmnViewerRef = useRef<BpmnViewer | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
  const [highlightedActivityId, setHighlightedActivityId] = useState<string | null>(null);
  const [bpmnError, setBpmnError] = useState<string | null>(null);
  const [currentZoom, setCurrentZoom] = useState<number>(1);

  // Função para agrupar variáveis por atividade
  const getVariablesByActivity = (activityInstanceId: string): ProcessVariable[] => {
    return variables.filter(variable => variable.activity_instance_id === activityInstanceId);
  };

  // Função para obter nome da atividade pelo ID
  const getActivityName = (activityId: string): string => {
    const event = timeline.timeline.find(e => e.activityId === activityId);
    return event?.activityName || activityId;
  };

  // Função para verificar se uma atividade tem incidentes
  const hasIncidents = (activityId: string): boolean => {
    return incidents.some(
      incident => 
        incident.activity_id === activityId || 
        incident.failed_activity_id === activityId
    );
  };

  // Inicializar o visualizador BPMN
  useEffect(() => {
    if (!bpmnContainerRef.current || !bpmnXml) return;

    const viewer = new BpmnViewer({
      container: bpmnContainerRef.current,
      width: '100%',
      height: '600px'
    });

    bpmnViewerRef.current = viewer;

    viewer.importXML(bpmnXml)
      .then(() => {
        setBpmnError(null);
        
        // Configurar canvas para permitir navegação
        const canvas = viewer.get('canvas') as { zoom: (value?: string | number) => number; scrollToElement: (elementId: string, margin?: number) => void };
        
        // Ajustar zoom para fit-viewport com delay e validação
        setTimeout(() => {
          try {
            const initialZoom = canvas.zoom('fit-viewport');
            const validZoom = initialZoom && initialZoom > 0 ? initialZoom : 1.0;
            setCurrentZoom(validZoom);
            console.log('Zoom inicial definido:', validZoom);
          } catch (error) {
            console.warn('Erro ao ajustar zoom inicial:', error);
            setCurrentZoom(1.0);
          }
        }, 100);
        
        // Aplicar estilos iniciais
        highlightExecutedActivities();
      })
      .catch((err: Error) => {
        console.error('Erro ao carregar BPMN:', err);
        setBpmnError('Erro ao carregar o diagrama BPMN');
      });

    return () => {
      viewer.destroy();
    };
  }, [bpmnXml]);

  // Destacar atividades executadas no diagrama
  const highlightExecutedActivities = () => {
    if (!bpmnViewerRef.current) return;

    try {
      const canvas = bpmnViewerRef.current.get('canvas') as { 
        removeMarker: (elementId: string, marker: string) => void; 
        addMarker: (elementId: string, marker: string) => void; 
        zoom: (value?: string | number) => number; 
        scrollToElement: (elementId: string, margin?: number) => void 
      };
      const elementRegistry = bpmnViewerRef.current.get('elementRegistry') as { 
        getAll: () => Array<{ id: string; type: string }> 
      };

      // Obter IDs das atividades executadas
      const executedActivityIds = new Set(
        timeline.timeline
          .filter(event => event.activityId)
          .map(event => event.activityId!)
      );

      // Processar todos os elementos do diagrama
      elementRegistry.getAll().forEach((element: { id: string; type: string }) => {
        // Resetar todos os marcadores
        canvas.removeMarker(element.id, 'executed');
        canvas.removeMarker(element.id, 'not-executed');
        canvas.removeMarker(element.id, 'selected');
        canvas.removeMarker(element.id, 'has-incident');

        // Verificar se é uma atividade (não sequenceFlow, não gateways de sistema)
        const isActivity = element.type && (
          element.type.includes('Task') || 
          element.type.includes('Event') ||
          element.type.includes('Gateway')
        );

        if (isActivity) {
          // Verificar se a atividade tem incidentes
          const activityHasIncidents = hasIncidents(element.id);
          
          if (activityHasIncidents) {
            // Atividade com incidente - destacar em vermelho (prioridade)
            canvas.addMarker(element.id, 'has-incident');
          } else if (executedActivityIds.has(element.id)) {
            // Atividade executada - destacar em verde
            canvas.addMarker(element.id, 'executed');
          } else {
            // Atividade não executada - marcar como não executada
            canvas.addMarker(element.id, 'not-executed');
          }
        }
      });

      // Destacar atividade selecionada (apenas se foi executada)
      if (selectedEvent?.activityId && executedActivityIds.has(selectedEvent.activityId)) {
        canvas.addMarker(selectedEvent.activityId, 'selected');
      }
    } catch (error) {
      console.warn('Erro ao destacar atividades:', error);
    }
  };

  // Aplicar estilos quando as atividades mudam
  useEffect(() => {
    highlightExecutedActivities();
  }, [timeline, highlightedActivityId, selectedEvent, incidents]);

  // Adicionar estilos CSS para os marcadores e navegação
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Estilos para atividades executadas */
      .executed .djs-visual > :nth-child(1) {
        stroke: #4caf50 !important;
        stroke-width: 3px !important;
        fill: rgba(76, 175, 80, 0.15) !important;
        cursor: pointer !important;
      }
      
      /* Estilos para atividades NÃO executadas */
      .not-executed .djs-visual > :nth-child(1) {
        stroke: #bdbdbd !important;
        stroke-width: 2px !important;
        fill: rgba(189, 189, 189, 0.05) !important;
        opacity: 0.6 !important;
        cursor: not-allowed !important;
        stroke-dasharray: 5,5 !important;
      }
      
      /* Texto das atividades não executadas */
      .not-executed .djs-visual text {
        fill: #9e9e9e !important;
        opacity: 0.7 !important;
      }
      
      /* Estilos para atividades com incidente */
      .has-incident .djs-visual > :nth-child(1) {
        stroke: #f44336 !important;
        stroke-width: 4px !important;
        fill: rgba(244, 67, 54, 0.25) !important;
        cursor: pointer !important;
        animation: pulse-error 2s ease-in-out infinite;
      }
      
      /* Texto das atividades com incidente */
      .has-incident .djs-visual text {
        fill: #d32f2f !important;
        font-weight: bold !important;
      }
      
      /* Atividade selecionada */
      .selected .djs-visual > :nth-child(1) {
        stroke: #2196f3 !important;
        stroke-width: 4px !important;
        fill: rgba(33, 150, 243, 0.25) !important;
        cursor: pointer !important;
      }
      
      @keyframes pulse {
        0% { opacity: 1; }
        50% { opacity: 0.7; }
        100% { opacity: 1; }
      }
      
      @keyframes pulse-error {
        0% { 
          stroke-width: 4px;
          fill-opacity: 0.25;
        }
        50% { 
          stroke-width: 5px;
          fill-opacity: 0.35;
        }
        100% { 
          stroke-width: 4px;
          fill-opacity: 0.25;
        }
      }
      
      /* Estilos para navegação com mouse no BPMN */
      .djs-container {
        font-family: Arial, sans-serif;
        user-select: none;
      }
      
      .djs-canvas {
        cursor: grab !important;
        overflow: hidden !important;
      }
      
      .djs-canvas:active {
        cursor: grabbing !important;
      }
      
      .djs-canvas .viewport {
        pointer-events: all !important;
      }
      
      /* Habilitar scroll com mouse wheel */
      .djs-container .djs-canvas svg {
        pointer-events: all !important;
      }
      
      /* Garantir que pan e zoom funcionem */
      .djs-container .djs-canvas .viewport {
        pointer-events: all !important;
      }
      
      /* Hover apenas para atividades executadas */
      .executed .djs-visual > :nth-child(1):hover {
        stroke-width: 4px !important;
        fill: rgba(76, 175, 80, 0.25) !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Manipular clique em evento da timeline
  const handleEventClick = (event: TimelineEvent) => {
    setSelectedEvent(event);
    setHighlightedActivityId(null); // Limpar highlight já que não usamos mais
    
    // Centralizar no elemento se tiver activityId
    if (event.activityId && bpmnViewerRef.current) {
      try {
        const canvas = bpmnViewerRef.current.get('canvas') as { scrollToElement: (elementId: string, margin?: number) => void };
        canvas.scrollToElement(event.activityId, 100);
      } catch (error) {
        console.warn('Erro ao centralizar elemento:', error);
      }
    }
    
    onEventClick?.(event);
  };

  // Manipular clique em atividade no diagrama
  useEffect(() => {
    if (!bpmnViewerRef.current) return;

    try {
      const eventBus = bpmnViewerRef.current.get('eventBus') as { 
        on: (event: string, handler: (event: { element: { id: string; type: string } }) => void) => void; 
        off: (event: string, handler: (event: { element: { id: string; type: string } }) => void) => void 
      };
      
      // Obter IDs das atividades executadas
      const executedActivityIds = new Set(
        timeline.timeline
          .filter(event => event.activityId)
          .map(event => event.activityId!)
      );
      
      const handleElementClick = (event: { element: { id: string; type: string } }) => {
        const element = event.element;
        
        // Verificar se não é sequenceFlow e se foi executada
        if (element.type !== 'bpmn:SequenceFlow') {
          
          // Apenas permitir seleção de atividades executadas
          if (executedActivityIds.has(element.id)) {
            // Se já está selecionada, desselecionar
            if (selectedEvent?.activityId === element.id) {
              setSelectedEvent(null);
              setHighlightedActivityId(null);
            } else {
              // Selecionar nova atividade
              setHighlightedActivityId(null); // Limpar highlight da timeline
              onActivityClick?.(element.id);
              
              // Encontrar evento relacionado
              const relatedEvent = timeline.timeline.find(e => e.activityId === element.id);
              if (relatedEvent) {
                setSelectedEvent(relatedEvent);
              }
            }
          } else {
            // Atividade não executada - mostrar feedback visual ou não fazer nada
            console.log(`Atividade ${element.id} não foi executada nesta instância`);
          }
        }
      };

      eventBus.on('element.click', handleElementClick);

      return () => {
        eventBus.off('element.click', handleElementClick);
      };
    } catch (error) {
      console.warn('Erro ao configurar event handlers:', error);
    }
  }, [timeline, onActivityClick]);

  // Obter ícone do status
  const getStatusIcon = (status: EventStatus) => {
    switch (status) {
      case EventStatus.COMPLETED:
        return <CheckCircle sx={{ color: STATUS_COLORS[status] }} />;
      case EventStatus.ACTIVE:
        return <PlayArrow sx={{ color: STATUS_COLORS[status] }} />;
      case EventStatus.FAILED:
        return <Error sx={{ color: STATUS_COLORS[status] }} />;
      case EventStatus.CANCELLED:
        return <Error sx={{ color: STATUS_COLORS[status] }} />;
      default:
        return <AccessTime sx={{ color: STATUS_COLORS[EventStatus.STARTED] }} />;
    }
  };

  // Formatar tipo de atividade para exibição amigável
  const formatActivityType = (activityType?: string) => {
    if (!activityType) return 'N/A';
    
    const typeMap: Record<string, string> = {
      'startEvent': 'Evento de Início',
      'endEvent': 'Evento de Fim',
      'noneEndEvent': 'Evento de Fim',
      'userTask': 'Tarefa do Usuário',
      'serviceTask': 'Tarefa de Serviço',
      'scriptTask': 'Tarefa de Script',
      'businessRuleTask': 'Regra de Negócio',
      'task': 'Tarefa',
      'parallelGateway': 'Gateway Paralelo',
      'exclusiveGateway': 'Gateway Exclusivo',
      'boundaryTimer': 'Timer de Limite',
      'intermediateCatchEvent': 'Evento Intermediário'
    };
    
    return typeMap[activityType] || activityType;
  };

  // Formatar duração
  const formatDuration = (durationMs: number | null): string => {
    if (!durationMs) return 'N/A';
    
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h ${minutes % 60}m`;
    if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  // Filtrar eventos se necessário
  const filteredEvents = showOnlyExecutedPath 
    ? timeline.timeline.filter(event => 
        timeline.statistics.criticalPath.events.includes(event.id)
      )
    : timeline.timeline;

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2}>
        {/* Painel do diagrama BPMN */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '650px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountTree />
                Diagrama do Processo
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                <Tooltip title={`Zoom In (${Math.round(currentZoom * 100)}%)`}>
                  <IconButton onClick={() => {
                    if (bpmnViewerRef.current) {
                      try {
                        const canvas = bpmnViewerRef.current.get('canvas') as { zoom: (value?: number) => number };
                        
                        // Verificar se zoom atual é válido
                        if (currentZoom >= 5.0) {
                          console.log('Zoom máximo atingido:', currentZoom);
                          return;
                        }
                        
                        // Usar incremento relativo mais seguro
                        const currentActualZoom = canvas.zoom(); // Obter zoom real do canvas
                        const newZoom = Math.min(5.0, currentActualZoom * 1.2);
                        console.log('Zoom In - Canvas atual:', currentActualZoom, 'Novo:', newZoom);
                        
                        canvas.zoom(newZoom);
                        setCurrentZoom(newZoom);
                      } catch (error) {
                        console.warn('Erro ao fazer zoom in:', error);
                      }
                    }
                  }}>
                    <ZoomIn />
                  </IconButton>
                </Tooltip>
                <Tooltip title={`Zoom Out (${Math.round(currentZoom * 100)}%)`}>
                  <IconButton onClick={() => {
                    if (bpmnViewerRef.current) {
                      try {
                        const canvas = bpmnViewerRef.current.get('canvas') as { zoom: (value?: number) => number };
                        
                        // Verificar se zoom atual é válido
                        if (currentZoom <= 0.1) {
                          console.log('Zoom mínimo atingido:', currentZoom);
                          return;
                        }
                        
                        // Usar incremento relativo mais seguro
                        const currentActualZoom = canvas.zoom(); // Obter zoom real do canvas
                        const newZoom = Math.max(0.1, currentActualZoom * 0.8);
                        console.log('Zoom Out - Canvas atual:', currentActualZoom, 'Novo:', newZoom);
                        
                        canvas.zoom(newZoom);
                        setCurrentZoom(newZoom);
                      } catch (error) {
                        console.warn('Erro ao fazer zoom out:', error);
                      }
                    }
                  }}>
                    <ZoomOut />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Ajustar à viewport">
                  <IconButton onClick={() => {
                    if (bpmnViewerRef.current) {
                      try {
                        const canvas = bpmnViewerRef.current.get('canvas') as { zoom: (value: string | number) => number };
                        const newZoom = canvas.zoom('fit-viewport');
                        setCurrentZoom(newZoom);
                      } catch (error) {
                        console.warn('Erro ao ajustar zoom:', error);
                      }
                    }
                  }}>
                    <CenterFocusStrong />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Centralizar diagrama">
                  <IconButton onClick={() => {
                    if (bpmnViewerRef.current) {
                      try {
                        const canvas = bpmnViewerRef.current.get('canvas') as { zoom: (value: string | number) => number };
                        
                        // Resetar para fit-viewport que é mais confiável
                        const newZoom = canvas.zoom('fit-viewport');
                        setCurrentZoom(newZoom);
                      } catch (error) {
                        console.warn('Erro ao centralizar diagrama:', error);
                      }
                    }
                  }}>
                    <MyLocation />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            {bpmnError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {bpmnError}
              </Alert>
            ) : null}
            
            <Box 
              ref={bpmnContainerRef} 
              sx={{ 
                width: '100%', 
                height: '600px',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                overflow: 'hidden',
                position: 'relative',
                '& .djs-container': {
                  width: '100% !important',
                  height: '100% !important'
                },
                '& .djs-canvas': {
                  cursor: 'grab'
                },
                '& .djs-canvas:active': {
                  cursor: 'grabbing'
                }
              }} 
            />
            
          </Paper>
          
          {/* Legenda e Controles - Movido para fora do Paper */}
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
              <Chip 
                size="small" 
                label="✅ Executado (clicável)" 
                sx={{ 
                  bgcolor: 'rgba(76, 175, 80, 0.1)', 
                  border: '2px solid #4caf50',
                  color: '#4caf50'
                }} 
              />
              <Chip 
                size="small" 
                label="⏸️ Não Executado" 
                sx={{ 
                  bgcolor: 'rgba(189, 189, 189, 0.05)', 
                  border: '2px dashed #bdbdbd',
                  color: '#9e9e9e'
                }} 
              />
              <Chip 
                size="small" 
                label="🔵 Selecionado" 
                sx={{ 
                  bgcolor: 'rgba(33, 150, 243, 0.1)', 
                  border: '2px solid #2196f3',
                  color: '#2196f3'
                }} 
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              💡 Navegação: 🖱️ Arraste para mover | 🎡 Scroll para zoom | Controles: 🔍+ 🔍- 🎯 📍 | Apenas atividades executadas são clicáveis
            </Typography>
          </Box>
        </Grid>

        {/* Painel da timeline */}
        <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: '650px', overflow: 'hidden' }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Timeline />
                Timeline de Execução
              </Typography>

              {/* Estatísticas resumidas */}
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Speed />
                    Estatísticas
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total de Eventos
                      </Typography>
                      <Typography variant="h6">
                        {timeline.statistics.totalEvents}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Duração Total
                      </Typography>
                      <Typography variant="h6">
                        {formatDuration(timeline.statistics.totalDuration)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Gargalo Principal
                      </Typography>
                      <Typography variant="body2">
                        {timeline.statistics.criticalPath.bottlenecks[0]?.activityName || 'N/A'}
                      </Typography>
                      {timeline.statistics.criticalPath.bottlenecks[0] && (
                        <LinearProgress 
                          variant="determinate" 
                          value={timeline.statistics.criticalPath.bottlenecks[0].percentage}
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>


              {/* Lista de eventos */}
              <Box sx={{ 
                height: 'calc(650px - 140px)', // Altura restante após cabeçalho e estatísticas
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mt: 1,
                backgroundColor: 'background.paper',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'grey.100',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'grey.400',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: 'grey.500',
                  },
                },
              }}>
                <List dense sx={{ p: 0 }}>
                  {filteredEvents.map((event, index) => (
                  <React.Fragment key={event.id}>
                    <ListItem
                      button
                      selected={selectedEvent?.id === event.id}
                      onClick={() => handleEventClick(event)}
                      sx={{
                        borderLeft: `4px solid ${EVENT_COLORS[event.type]}`,
                        mb: 0.5,
                        borderRadius: 1,
                        bgcolor: selectedEvent?.id === event.id ? 'action.selected' : 'transparent'
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {getStatusIcon(event.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography component="span" variant="body2" sx={{ fontWeight: 'medium' }}>
                            {event.activityName || event.type}
                          </Typography>
                        }
                        secondary={
                          <span>
                            <Typography component="span" variant="caption" color="text.secondary">
                              {formatActivityType(event.activityType || undefined)} • {new Date(event.timestamp).toLocaleString('pt-BR')}
                            </Typography>
                            {event.duration && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                • {formatDuration(event.duration)}
                              </Typography>
                            )}
                          </span>
                        }
                      />
                    </ListItem>
                    {index < filteredEvents.length - 1 && <Divider variant="inset" />}
                  </React.Fragment>
                  ))}
                </List>
              </Box>
            </Paper>
          </Grid>
      </Grid>

      {/* Painel de detalhes do evento selecionado */}
      {selectedEvent && (
        <Paper sx={{ mt: 2, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Detalhes do Evento
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardHeader 
                  title="Informações Básicas"
                  titleTypographyProps={{ variant: 'subtitle1' }}
                />
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Tipo</Typography>
                      <Chip 
                        label={formatActivityType(selectedEvent.activityType || undefined)} 
                        size="small" 
                        sx={{ bgcolor: EVENT_COLORS[selectedEvent.type], color: 'white' }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <Chip 
                        icon={getStatusIcon(selectedEvent.status)}
                        label={selectedEvent.status} 
                        size="small" 
                        variant="outlined"
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Timestamp</Typography>
                      <Typography variant="body2">
                        {new Date(selectedEvent.timestamp).toLocaleString('pt-BR')}
                      </Typography>
                    </Box>
                    {selectedEvent.duration && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Duração</Typography>
                        <Typography variant="body2">
                          {formatDuration(selectedEvent.duration)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardHeader 
                  title="Detalhes Técnicos"
                  titleTypographyProps={{ variant: 'subtitle1' }}
                />
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedEvent.activityId && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Activity ID</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {selectedEvent.activityId}
                        </Typography>
                      </Box>
                    )}
                    {selectedEvent.executionId && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Execution ID</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {selectedEvent.executionId}
                        </Typography>
                      </Box>
                    )}
                    {selectedEvent.taskId && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Task ID</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {selectedEvent.taskId}
                        </Typography>
                      </Box>
                    )}
                    {selectedEvent.details.assignee && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Assignee</Typography>
                        <Typography variant="body2">
                          {selectedEvent.details.assignee}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Variáveis da Atividade */}
          {selectedEvent.details.activityInstanceId && (
            <Box sx={{ mt: 2 }}>
              <ActivityVariables
                variables={getVariablesByActivity(selectedEvent.details.activityInstanceId)}
                activityId={selectedEvent.activityId || ''}
                activityName={getActivityName(selectedEvent.activityId || '')}
                defaultExpanded={true}
              />
            </Box>
          )}

          {/* Incidentes da Atividade */}
          {selectedEvent.activityId && incidents.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <ActivityIncidents
                activityId={selectedEvent.activityId}
                incidents={incidents}
              />
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default BpmnTimeline;
